import AddIcon from "@mui/icons-material/Add";
import { Button, TextField } from "@mui/material";
import React from "react";
import {
  invoiceDesignTokens,
  invoiceFieldSx,
} from "../../Invoicing/InvoiceDesignSystem";
import InvoiceLineItemDeleteDialog from "../../Invoicing/InvoiceLineItemDeleteDialog";
import InvoiceSearchableSelect from "../../Invoicing/InvoiceSearchableSelect";
import {
  emptyEventAttendeeChoiceFields,
  type EventAttendeeChoiceFields,
} from "../eventBackendMappers";
import {
  LineItemDeleteButton,
  formatCurrencyFromCents,
  goldButtonSx,
} from "./eventSalesShared";
import EventInvoiceAttendeeFields from "./EventInvoiceAttendeeFields";
import type { EventAttendeeSuggestion } from "./eventAttendeeSuggestions";
import {
  createEventTicketInvoiceLine,
  formatEventTicketDiscountAmount,
  formatEventTicketDiscountPercentage,
  getEventInvoicePrimaryAttendeeId,
  getEventTicketLinePricing,
  limitEventTicketDiscountInput,
  resizeEventTicketLineAttendees,
  type EventTicketDiscountMode,
  type EventTicketInvoiceLine,
} from "./eventInvoiceCreateState";

export type EventInvoiceTicketTypeOption = Readonly<{
  attendeesPerTicket: number;
  code?: string | null;
  id: string;
  name: string;
  /** Create Event "No Tax": this ticket type is left out of tax. */
  noTax?: boolean | null;
  priceInCents: number;
}>;

type EventTicketInvoiceLinesEditorProps = Readonly<{
  choiceFieldsByTicketTypeId: Readonly<
    Record<string, EventAttendeeChoiceFields>
  >;
  disabled?: boolean;
  /** No event chosen yet: the design locks Ticket Type (D:3186, :13821). */
  ticketTypeLocked?: boolean;
  errors: Readonly<Record<string, string>>;
  lines: EventTicketInvoiceLine[];
  onChange: (lines: EventTicketInvoiceLine[]) => void;
  onClearError: (...keys: string[]) => void;
  attendeeSuggestions?: readonly EventAttendeeSuggestion[];
  attendeeCompanyOptions?: readonly string[];
  /** Design (11) TAX column: the invoice rule; a No Tax ticket type line
   * shows "No Tax" unless the customer is tax exempt. */
  taxPill?: Readonly<{ label: string; exempt: boolean }>;
  /** Design (11) Sub-Total / Tax / Total, shown under "Add Ticket Type". */
  totals?: React.ReactNode;
  ticketTypes: readonly EventInvoiceTicketTypeOption[];
}>;

const getTicketType = (
  ticketTypes: readonly EventInvoiceTicketTypeOption[],
  ticketTypeId: string,
) => ticketTypes.find((ticketType) => ticketType.id === ticketTypeId);

const ticketLineField38Sx = {
  marginTop: "2px",
  "& .MuiInputBase-root": {
    height: 38,
    minHeight: "38px !important",
  },
  "& .MuiInputBase-input": {
    padding: "8px 12px",
  },
} as const;

const ticketLineField40Sx = {
  marginTop: "1px",
  "& .MuiInputBase-root": {
    height: 40,
    minHeight: "40px !important",
  },
  "& .MuiInputBase-input": {
    padding: "9px 12px",
  },
} as const;

/**
 * Design (13) D:3803-3809: one bordered field with the amount input and a
 * "%" / "$" toggle. The active button is black with white text. Switching the
 * mode clears the value; % stops at 100 and $ at the line total.
 */
const EventTicketDiscountField = ({
  disabled,
  error,
  grossInCents,
  line,
  onChange,
  ticketName,
}: {
  disabled: boolean;
  error?: string;
  grossInCents: number;
  line: EventTicketInvoiceLine;
  onChange: (
    patch: Partial<Pick<EventTicketInvoiceLine, "discountAmount" | "discountMode" | "discountPercentage">>,
  ) => void;
  ticketName?: string;
}) => {
  const isAmount = line.discountMode === "$";
  const value = isAmount ? line.discountAmount : line.discountPercentage;
  const modeButton = (mode: EventTicketDiscountMode, title: string, withDivider: boolean) => {
    const active = line.discountMode === mode;
    return (
      <button
        aria-pressed={active}
        className={`w-[28px] shrink-0 cursor-pointer border-0 p-0 text-[13px] font-semibold disabled:cursor-default ${
          withDivider ? "border-l border-solid border-[#9E9E9E]" : ""
        } ${active ? "bg-[#0A0A0A] text-white" : "bg-white text-[#727272]"}`}
        disabled={disabled}
        onClick={() => {
          if (!active) onChange({ discountAmount: "", discountMode: mode, discountPercentage: "" });
        }}
        title={title}
        type="button"
      >
        {mode}
      </button>
    );
  };

  return (
    <div className="mt-px min-w-0">
      <div
        className={`flex h-[40px] min-w-0 overflow-hidden rounded-lg border border-solid bg-white ${
          error ? "border-[#E24C4C]" : "border-[#9E9E9E]"
        }`}
      >
        <input
          aria-invalid={Boolean(error)}
          aria-label={`Discount for ${ticketName || "ticket line"}`}
          className="m-0 w-full min-w-0 border-0 bg-transparent px-2 py-[10px] text-center text-sm leading-[normal] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E]"
          disabled={disabled}
          inputMode="decimal"
          onBlur={() =>
            onChange(
              isAmount
                ? { discountAmount: formatEventTicketDiscountAmount(line.discountAmount) }
                : { discountPercentage: formatEventTicketDiscountPercentage(line.discountPercentage) },
            )
          }
          onChange={({ target }) => {
            const limited = limitEventTicketDiscountInput({
              grossInCents,
              mode: line.discountMode,
              value: target.value,
            });
            onChange(isAmount ? { discountAmount: limited } : { discountPercentage: limited });
          }}
          placeholder={isAmount ? "0.00" : "0.0"}
          type="text"
          value={value}
        />
        <div className="flex shrink-0 border-l border-solid border-[#9E9E9E]">
          {modeButton("%", "Percentage discount", false)}
          {modeButton("$", "Dollar amount discount", true)}
        </div>
      </div>
      {error && <p className="m-0 mt-1 text-xs leading-[1.4] text-[#E24C4C]">{error}</p>}
    </div>
  );
};

const EventTicketInvoiceLinesEditor = ({
  choiceFieldsByTicketTypeId,
  disabled = false,
  ticketTypeLocked = false,
  errors,
  lines,
  onChange,
  onClearError,
  attendeeSuggestions = [],
  attendeeCompanyOptions = [],
  taxPill,
  ticketTypes,
  totals,
}: EventTicketInvoiceLinesEditorProps): React.ReactElement => {
  const [lineIdPendingDelete, setLineIdPendingDelete] = React.useState<
    string | null
  >(null);

  const updateLine = (
    lineId: string,
    updater: (line: EventTicketInvoiceLine) => EventTicketInvoiceLine,
  ) =>
    onChange(lines.map((line) => (line.id === lineId ? updater(line) : line)));

  // Attendee 1 of the whole invoice is the only attendee whose email is required.
  const primaryAttendeeId = getEventInvoicePrimaryAttendeeId(
    lines.map((line) => line.attendees),
  );

  const linePresentations = lines.map((line) => {
    const selectedTicket = getTicketType(ticketTypes, line.ticketTypeId);
    const pricing = getEventTicketLinePricing({
      catalogUnitPriceInCents: selectedTicket?.priceInCents || 0,
      line,
    });
    const selectedElsewhere = new Set(
      lines
        .filter((otherLine) => otherLine.id !== line.id)
        .map((otherLine) => otherLine.ticketTypeId)
        .filter(Boolean),
    );

    return {
      line,
      selectedElsewhere,
      selectedTicket,
      grossInCents: (selectedTicket?.priceInCents || 0) * line.quantity,
      subtotalInCents: selectedTicket ? pricing.subtotalInCents : 0,
    };
  });

  return (
    <div className="space-y-0">
      <div className="overflow-x-auto" data-ticket-lines-header-scroller>
        <div className="min-w-[1050px]">
          <div className="grid grid-cols-[minmax(90px,.6fr)_minmax(180px,2fr)_minmax(80px,.5fr)_minmax(115px,.8fr)_minmax(150px,.9fr)_minmax(100px,.6fr)_minmax(115px,.8fr)_46px] items-center gap-3 rounded-t bg-[#0B9DF2]">
            {[
              "Code",
              "Description",
              "Qty",
              "Unit Price",
              "Discount (% / $)",
              "Tax",
              "Sub-Total",
              "",
            ].map((heading, index) => (
              <div
                className={`px-[14px] py-[11px] text-xs font-bold uppercase tracking-wide text-white ${
                  index >= 2 && index <= 5 ? "text-center" : ""
                }`}
                key={`${heading}-${index}`}
              >
                {heading}
              </div>
            ))}
          </div>
        </div>
      </div>

      {linePresentations.map(
        ({ grossInCents, line, selectedElsewhere, selectedTicket, subtotalInCents }) => (
          <div className="space-y-3" key={line.id}>
            <div
              className="overflow-x-auto"
              data-ticket-line-scroller={line.id}
            >
              <div className="min-w-[1050px]">
                <div className="grid grid-cols-[minmax(90px,.6fr)_minmax(180px,2fr)_minmax(80px,.5fr)_minmax(115px,.8fr)_minmax(150px,.9fr)_minmax(100px,.6fr)_minmax(115px,.8fr)_46px] items-start gap-3 py-3">
                  <TextField
                    disabled
                    placeholder="Code"
                    size="small"
                    sx={[
                      invoiceFieldSx,
                      ticketLineField38Sx,
                      {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: invoiceDesignTokens.disabled,
                        },
                      },
                    ]}
                    value={selectedTicket?.code || ""}
                  />
                  <InvoiceSearchableSelect
                    id={`ticket-type-${line.id}`}
                    options={ticketTypes.filter(
                      (ticketType) =>
                        ticketType.id === line.ticketTypeId ||
                        !selectedElsewhere.has(ticketType.id),
                    )}
                    getOptionKey={(option) => option.id}
                    getOptionLabel={(option) => option.name}
                    value={selectedTicket || null}
                    disabled={disabled || ticketTypeLocked}
                    onChange={(ticketType) => {
                      const nextTicketTypeId = ticketType?.id || "";
                      // Design D:11227-11232: picking a type on an empty
                      // line starts it at 1 ticket.
                      const quantity =
                        ticketType && line.quantity < 1
                          ? 1
                          : line.quantity;
                      updateLine(line.id, (current) => ({
                        ...current,
                        attendees: resizeEventTicketLineAttendees({
                          attendees: current.attendees,
                          attendeeCount:
                            quantity *
                            Math.max(
                              1,
                              Math.floor(ticketType?.attendeesPerTicket || 1),
                            ),
                        }),
                        quantity,
                        ticketTypeId: nextTicketTypeId,
                      }));
                      onClearError(`ticketType.${line.id}`, `quantity.${line.id}`, "ticketLines");
                    }}
                    error={Boolean(errors[`ticketType.${line.id}`])}
                    helperText={errors[`ticketType.${line.id}`]}
                    placeholder={
                      ticketTypeLocked
                        ? "Select an event first"
                        : "Select Ticket Type"
                    }
                    searchPlaceholder="Search Ticket Type"
                  />
                  <TextField
                    error={Boolean(errors[`quantity.${line.id}`])}
                    helperText={errors[`quantity.${line.id}`]}
                    inputProps={{
                      "aria-label": `Quantity for ${selectedTicket?.name || "ticket line"}`,
                      min: 1,
                      step: 1,
                    }}
                    onChange={({ target }) => {
                      const quantity = Math.max(
                        0,
                        Math.floor(Number(target.value) || 0),
                      );
                      updateLine(line.id, (current) => ({
                        ...current,
                        attendees: resizeEventTicketLineAttendees({
                          attendees: current.attendees,
                          attendeeCount:
                            quantity *
                            Math.max(
                              1,
                              Math.floor(
                                selectedTicket?.attendeesPerTicket || 1,
                              ),
                            ),
                        }),
                        quantity,
                      }));
                      onClearError(`quantity.${line.id}`, "ticketLines");
                    }}
                    size="small"
                    sx={[
                      invoiceFieldSx,
                      ticketLineField40Sx,
                      { "& input": { textAlign: "center" } },
                    ]}
                    type="number"
                    value={line.quantity}
                  />
                  <TextField
                    disabled
                    inputProps={{
                      "aria-label": `Unit price for ${selectedTicket?.name || "ticket line"}`,
                    }}
                    size="small"
                    sx={[
                      invoiceFieldSx,
                      ticketLineField38Sx,
                      {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: invoiceDesignTokens.disabled,
                        },
                        "& input": { textAlign: "center" },
                      },
                    ]}
                    value={
                      selectedTicket
                        ? formatCurrencyFromCents(selectedTicket.priceInCents)
                        : formatCurrencyFromCents(0)
                    }
                  />
                  <EventTicketDiscountField
                    disabled={disabled}
                    error={errors[`discount.${line.id}`]}
                    grossInCents={grossInCents}
                    line={line}
                    ticketName={selectedTicket?.name}
                    onChange={(patch) => {
                      updateLine(line.id, (current) => ({ ...current, ...patch }));
                      onClearError(`discount.${line.id}`, "ticketLines");
                    }}
                  />
                  <div className="flex justify-center pt-[7px]">
                    <span
                      className={`whitespace-nowrap rounded-md px-2.5 py-[5px] text-[13px] font-semibold leading-[normal] ${
                        selectedTicket && taxPill?.exempt
                          ? "bg-[#FDECEC] text-[#F21B1B]"
                          : "bg-[#EDEDED] text-[#727272]"
                      }`}
                    >
                      {selectedTicket && taxPill
                        ? selectedTicket.noTax && !taxPill.exempt
                          ? "No Tax"
                          : taxPill.label
                        : "\u2014"}
                    </span>
                  </div>
                  <div className="px-3 pt-2 text-[15px] font-bold text-gray-950">
                    {formatCurrencyFromCents(subtotalInCents)}
                  </div>
                  <LineItemDeleteButton
                    ariaLabel={`Delete ${selectedTicket?.name || "ticket"} line`}
                    onClick={() => setLineIdPendingDelete(line.id)}
                  />
                </div>
              </div>
            </div>

          </div>
        ),
      )}

      {/* Design D:3200-3202: the button sits right under the rows, before
          the attendee sections. */}
      <div className="mb-3 flex justify-end">
        <Button
          disabled={disabled || ticketTypeLocked}
          onClick={() => {
            onChange([...lines, createEventTicketInvoiceLine()]);
            onClearError("ticketLines");
          }}
          startIcon={<AddIcon />}
          sx={goldButtonSx}
          variant="contained"
        >
          Add Ticket Type
        </Button>
      </div>
      {totals}

      {linePresentations.map(({ line, selectedTicket }) =>
        selectedTicket && line.attendees.length > 0 ? (
          <div data-ticket-line-attendees={line.id} key={line.id}>
            <EventInvoiceAttendeeFields
              attendees={line.attendees}
              choiceFields={
                choiceFieldsByTicketTypeId[line.ticketTypeId] ||
                emptyEventAttendeeChoiceFields
              }
              disabled={disabled}
              errorKeyPrefix={`attendees.${line.id}`}
              errors={errors}
              onChange={(attendees) => {
                updateLine(line.id, (current) => ({
                  ...current,
                  attendees,
                }));
              }}
              onClearError={onClearError}
              primaryAttendeeId={primaryAttendeeId}
              suggestions={attendeeSuggestions}
              companyOptions={attendeeCompanyOptions}
              title={selectedTicket.name}
            />
            {errors[`attendees.${line.id}`] ? (
              <p className="text-xs font-semibold text-red-600">
                {errors[`attendees.${line.id}`]}
              </p>
            ) : null}
          </div>
        ) : null,
      )}
      <InvoiceLineItemDeleteDialog
        onCancel={() => setLineIdPendingDelete(null)}
        onConfirm={() => {
          if (lineIdPendingDelete) {
            onChange(lines.filter((item) => item.id !== lineIdPendingDelete));
            onClearError("ticketLines");
          }
          setLineIdPendingDelete(null);
        }}
        open={Boolean(lineIdPendingDelete)}
      />
    </div>
  );
};

export default EventTicketInvoiceLinesEditor;
